// @ts-nocheck
import React, { useRef, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Camera, RotateCcw, Download, Upload } from 'lucide-react';

interface PhotoCaptureProps {
  onPhotoChange: (photoData: string | null) => void;
  width?: number;
  height?: number;
}

export const PhotoCaptureComponent: React.FC<PhotoCaptureProps> = ({
  onPhotoChange,
  width = 400,
  height = 300
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  const startCamera = async () => {
    try {
      setError(null);
      
      // Configurações mais robustas para a câmera
      const constraints = {
        video: {
          width: { ideal: width, min: 320, max: 1280 },
          height: { ideal: height, min: 240, max: 720 },
          facingMode: 'user',
          frameRate: { ideal: 30, min: 15 }
        },
        audio: false
      };

      // Timeout para evitar travamento
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Timeout ao acessar câmera')), 10000);
      });

      const mediaStream = await Promise.race([
        navigator.mediaDevices.getUserMedia(constraints),
        timeoutPromise
      ]);

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        
        // Aguardar o vídeo carregar
        await new Promise((resolve) => {
          if (videoRef.current) {
            videoRef.current.onloadedmetadata = resolve;
          }
        });
        
        setStream(mediaStream);
        setIsCameraActive(true);
      }
    } catch (err) {
      console.error('Erro ao acessar câmera:', err);
      let errorMessage = 'Não foi possível acessar a câmera.';
      
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          errorMessage = 'Permissão negada. Permita o acesso à câmera e tente novamente.';
        } else if (err.name === 'NotFoundError') {
          errorMessage = 'Câmera não encontrada. Verifique se há uma câmera conectada.';
        } else if (err.message.includes('Timeout')) {
          errorMessage = 'Timeout ao acessar câmera. Tente novamente.';
        }
      }
      
      setError(errorMessage);
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
      setIsCameraActive(false);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      if (context) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        context.drawImage(video, 0, 0);

        const photoData = canvas.toDataURL('image/jpeg', 0.8);
        setCapturedPhoto(photoData);
        onPhotoChange(photoData);
      }
    }
  };

  const retakePhoto = () => {
    setCapturedPhoto(null);
    onPhotoChange(null);
  };

  const downloadPhoto = () => {
    if (capturedPhoto) {
      const link = document.createElement('a');
      link.download = 'foto_assinante.jpg';
      link.href = capturedPhoto;
      link.click();
    }
  };

  const uploadPhoto = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const photoData = e.target?.result as string;
        setCapturedPhoto(photoData);
        onPhotoChange(photoData);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle className="text-lg font-semibold">
          📸 Captura de Foto
        </CardTitle>
        <p className="text-sm text-gray-600">
          Tire uma foto para confirmar sua identidade
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {!capturedPhoto ? (
          <div className="space-y-4">
            {!isCameraActive ? (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <Camera className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600 mb-4">
                  Clique no botão abaixo para ativar a câmera
                </p>
                <Button onClick={startCamera} className="w-full">
                  <Camera className="w-4 h-4 mr-2" />
                  Ativar Câmera
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-2">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full rounded"
                    style={{ width: `${width}px`, height: `${height}px` }}
                  />
                </div>
                
                <div className="flex gap-2">
                  <Button onClick={capturePhoto} className="flex-1">
                    <Camera className="w-4 h-4 mr-2" />
                    Tirar Foto
                  </Button>
                  <Button onClick={stopCamera} variant="outline">
                    Parar Câmera
                  </Button>
                </div>
              </div>
            )}

            <div className="relative">
              <input
                type="file"
                accept="image/*"
                onChange={uploadPhoto}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              <Button variant="outline" className="w-full">
                <Upload className="w-4 h-4 mr-2" />
                Carregar Foto
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-2">
              <img
                src={capturedPhoto}
                alt="Foto capturada"
                className="w-full rounded"
                style={{ width: `${width}px`, height: `${height}px`, objectFit: 'cover' }}
              />
            </div>
            
            <div className="flex gap-2">
              <Button onClick={retakePhoto} variant="outline" className="flex-1">
                <RotateCcw className="w-4 h-4 mr-2" />
                Nova Foto
              </Button>
              <Button onClick={downloadPhoto} variant="outline">
                <Download className="w-4 h-4 mr-2" />
                Baixar
              </Button>
            </div>
            
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
              ✅ Foto capturada com sucesso!
            </div>
          </div>
        )}

        <canvas ref={canvasRef} style={{ display: 'none' }} />
        
        <div className="text-xs text-gray-500">
          <p>💡 Dicas:</p>
          <ul className="list-disc list-inside mt-1 space-y-1">
            <li>Posicione-se bem iluminado</li>
            <li>Olhe diretamente para a câmera</li>
            <li>Mantenha uma expressão neutra</li>
            <li>Você pode carregar uma foto existente</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};
